import React, { memo, useCallback, useEffect } from "react";
import { Icon } from "@iconify/react";
import { useSelector } from "react-redux";
import styles from "./Modal.module.css";

const ContactModal = memo(({ isOpen, onClose }) => {
  const { websiteSetting } = useSelector((state) => state.user);

  // Extract Line and Telegram URLs from websiteSetting
  const settings = Array.isArray(websiteSetting)
    ? websiteSetting[0]
    : websiteSetting?.data?.[0] || {};
  const lineUrl = settings?.lineUrl || "https://line.me/";
  const teleUrl = settings?.teleUrl || "https://telegram.org/";
  const dynamicChannels = Array.isArray(settings?.contactChannels)
    ? settings.contactChannels
    : [];

  const contactChannels =
    dynamicChannels.length > 0
      ? dynamicChannels
      : [
          {
            channelType: "line",
            contactName: "Line Official",
            contactUrl: lineUrl,
          },
          {
            channelType: "telegram",
            contactName: "Telegram",
            contactUrl: teleUrl,
          },
        ];

  const handleOverlayClick = useCallback(
    (e) => {
      if (e.target === e.currentTarget) {
        onClose();
      }
    },
    [onClose],
  );

  const handleEscapeKey = useCallback(
    (e) => {
      if (e.key === "Escape") {
        onClose();
      }
    },
    [onClose],
  );

  useEffect(() => {
    if (isOpen) {
      document.addEventListener("keydown", handleEscapeKey);
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "unset";
    }

    return () => {
      document.removeEventListener("keydown", handleEscapeKey);
      document.body.style.overflow = "unset";
    };
  }, [isOpen, handleEscapeKey]);

  if (!isOpen) return null;

  return (
    <div className={styles.overlay} onClick={handleOverlayClick}>
      <div className={styles.modal}>
        <div className={styles.header}>
          <h2 className={styles.title}>📞 ช่องทางติดต่อ</h2>
          <button className={styles.closeButton} onClick={onClose}>
            ✕
          </button>
        </div>

        <div className={styles.content}>
          <p
            style={{
              textAlign: "center",
              color: "rgba(255,255,255,0.45)",
              fontSize: 13,
              margin: "0 0 20px 0",
              fontWeight: 500,
              lineHeight: 1.6,
            }}
          >
            ทีมงานพร้อมดูแลคุณตลอด 24 ชั่วโมง เลือกช่องทางที่สะดวก
          </p>

          <div className={styles.contactInfo}>
            {contactChannels.map((channel, index) => {
              const isLine = channel.channelType === "line";
              const iconSrc = isLine
                ? "https://space.mil-168.com/line.webp"
                : "https://space.mil-168.com/telegram.webp";
              const titleColor = isLine ? "#4ade80" : "#38bdf8";
              const itemTitle =
                channel.contactName || (isLine ? "LINE" : "Telegram");
              const itemDesc = isLine
                ? "แชทสดกับทีมงานผ่านไลน์"
                : "ส่งข้อความผ่านเทเลแกรม";

              return (
                <div
                  key={`${channel.channelType}-${channel.contactUrl}-${index}`}
                  className={styles.contactItem}
                  onClick={() => window.open(channel.contactUrl, "_blank")}
                >
                  <div
                    style={{
                      width: 52,
                      height: 52,
                      borderRadius: 14,
                      background: isLine
                        ? "linear-gradient(135deg, rgba(0,185,0,0.15), rgba(0,185,0,0.05))"
                        : "linear-gradient(135deg, rgba(0,136,204,0.15), rgba(0,136,204,0.05))",
                      border: isLine
                        ? "1px solid rgba(0,185,0,0.2)"
                        : "1px solid rgba(0,136,204,0.2)",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      flexShrink: 0,
                    }}
                  >
                    <img
                      src={iconSrc}
                      alt={itemTitle}
                      style={{ width: 36, height: 36, borderRadius: 8 }}
                    />
                  </div>
                  <div>
                    <h3
                      className={styles.contactTitle}
                      style={{ color: titleColor }}
                    >
                      {itemTitle}
                    </h3>
                    <p className={styles.contactText}>{itemDesc}</p>
                  </div>
                  <Icon
                    icon="solar:arrow-right-linear"
                    style={{
                      marginLeft: "auto",
                      fontSize: 20,
                      color: "rgba(255,255,255,0.25)",
                    }}
                  />
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
});

ContactModal.displayName = "ContactModal";

export default ContactModal;
